import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { Role } from '@revelio/data-access';

export interface BulkUserPayload {
  name: string;
  email: string;
  password: string;
  active: true;
  client_name: string;
  role: Role.ClientUser;
}

export interface BulkUserFailure {
  recordNumber: number;
  name: string;
  email: string;
  reason: string;
}

export interface AddBulkUsersResult {
  total: number;
  succeeded: number;
  failed: number;
  failures: BulkUserFailure[];
}

export const MAX_BULK_USERS = 1_000;

const getErrorMessage = async (response: Response): Promise<string> => {
  const responseText = await response.text();
  if (!responseText) {
    return response.statusText || 'Bulk user request failed';
  }

  try {
    const body: unknown = JSON.parse(responseText);
    if (
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      (typeof body.message === 'string' || Array.isArray(body.message))
    ) {
      return Array.isArray(body.message)
        ? body.message.join(' ')
        : body.message;
    }
  } catch {
    return responseText;
  }

  return responseText;
};

export const addBulkUsers = async (
  users: BulkUserPayload[]
): Promise<AddBulkUsersResult> => {
  if (users.length > MAX_BULK_USERS) {
    throw new Error(
      `Bulk user uploads are limited to ${MAX_BULK_USERS.toLocaleString('en-US')} users.`
    );
  }

  const csrfToken = getAuthDashMetaCsrfToken();
  const response = await fetch(`${DASH_META_ROOT}/api/admin/add-bulk-user`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'request-id': crypto.randomUUID(),
      'x-surf-token-dash-meta': csrfToken ?? '',
    },
    body: JSON.stringify({ users }),
  });

  if (!response.ok) {
    throw new Error(await getErrorMessage(response));
  }

  return response.json();
};
